import { useEffect, useLayoutEffect } from 'react';

const DOMAIN = 'https://bodybycc.fr';

/**
 * Sets canonical URL and OG URL meta tag.
 */
export function useCanonical(path: string) {
  useEffect(() => {
    const url = `${DOMAIN}${path}`;

    // Canonical
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', url);

    // OG URL
    let ogUrl = document.querySelector('meta[property="og:url"]') as HTMLMetaElement | null;
    if (!ogUrl) {
      ogUrl = document.createElement('meta');
      ogUrl.setAttribute('property', 'og:url');
      document.head.appendChild(ogUrl);
    }
    ogUrl.setAttribute('content', url);
  }, [path]);
}

/**
 * Ensures a meta tag exists and sets its content.
 */
function ensureMeta(attr: string, key: string, value: string) {
  const selector = `meta[${attr}="${key}"]`;
  const allMatching = document.head.querySelectorAll(selector);

  let el: HTMLMetaElement;
  if (allMatching.length > 0) {
    el = allMatching[0] as HTMLMetaElement;
    // Remove duplicates
    for (let i = 1; i < allMatching.length; i++) {
      allMatching[i].remove();
    }
  } else {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }

  el.setAttribute('content', value);
  el.content = value;
}

/**
 * Sets page title, meta description, OG and Twitter tags.
 * Uses useLayoutEffect to ensure meta tags are set BEFORE the browser paints,
 * so crawlers with JS rendering always see the correct values.
 */
export function usePageMeta(opts: { title: string; description: string; ogImage?: string }) {
  // Use useLayoutEffect for synchronous DOM updates before paint
  useLayoutEffect(() => {
    document.title = opts.title;

    ensureMeta('name', 'description', opts.description);
    ensureMeta('property', 'og:title', opts.title);
    ensureMeta('property', 'og:description', opts.description);
    ensureMeta('name', 'twitter:title', opts.title);
    ensureMeta('name', 'twitter:description', opts.description);

    if (opts.ogImage) {
      ensureMeta('property', 'og:image', opts.ogImage);
      ensureMeta('name', 'twitter:image', opts.ogImage);
    }
  }, [opts.title, opts.description, opts.ogImage]);
}
